import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Mail, Plus, Trash2 } from 'lucide-react';
import {
  api,
  type CellularNoticeList,
  type NoticeCase,
  type NoticeEvent,
  type NoticeNextStep,
  type NoticeRecipient,
  type NoticeState,
  type NoticeUsage,
} from '../lib/api';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Drawer } from '../components/ui/Drawer';
import { InfoTip } from '../components/ui/InfoTip';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SortableHeader } from '../components/ui/SortableHeader';
import { Textarea } from '../components/ui/Textarea';
import { useSort } from '../lib/sort';
import { formatDateTime, formatNumber, formatRelative, formatUsd } from '../lib/format';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/use-auth';

type Row = CellularNoticeList['rows'][number];

/** Decimal units, as Hologram bills. */
function formatData(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  return `${value.toFixed(unit === 0 || value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[unit]}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const STATE_LABEL: Record<NoticeState | 'flagged', string> = {
  flagged: 'Flagged',
  undecided: 'Undecided',
  notify: 'Notifying',
  declined: 'Not notifying',
  resolved: 'Resolved',
};

function StateBadge({ state }: { state: NoticeState | 'flagged' }) {
  const variant =
    state === 'notify' ? 'warning' : state === 'declined' ? 'secondary' : state === 'resolved' ? 'success' : 'outline';
  return (
    <Badge variant={variant} className="whitespace-nowrap">
      {STATE_LABEL[state]}
    </Badge>
  );
}

function NextStepText({ next, sendTime }: { next: NoticeNextStep; sendTime: string }) {
  switch (next.kind) {
    case 'decide':
      return <span className="text-caution">Decide whether to notify</span>;
    case 'not-notifying':
      return <span className="text-muted-foreground">—</span>;
    case 'add-recipients':
      return <span className="text-caution">Add recipients</span>;
    case 'waiting-days':
      return (
        <span className="text-muted-foreground">
          First email after {next.daysLeft} more day{next.daysLeft === 1 ? '' : 's'} on 4G
        </span>
      );
    case 'due':
      return (
        <span className="font-medium">
          {next.email === 'initial' ? 'First notice' : 'Reminder'} at {sendTime} next run
        </span>
      );
    case 'reminder-on':
      return <span className="text-muted-foreground">Reminder {formatDateTime(next.at)}</span>;
  }
}

function Excessive({ usage, threshold }: { usage: NoticeUsage; threshold: number }) {
  if (usage.excessive === 'over') {
    return <Badge variant="destructive">over ${threshold}</Badge>;
  }
  if (usage.excessive === 'projected') {
    return (
      <span title="Projected from the last 7 days’ actual spend — an estimate">
        <Badge variant="warning">projected over ${threshold}</Badge>
      </span>
    );
  }
  return null;
}

const getValue = (row: Row, key: string): unknown => {
  switch (key) {
    case 'customer':
      return row.usage?.label ?? row.notice?.label ?? row.key;
    case 'state':
      return ['undecided', 'notify', 'declined', 'resolved'].indexOf(row.notice?.state ?? 'undecided');
    case 'days':
      return row.usage?.daysOnFourG ?? -1;
    case 'data':
      return row.usage?.monthBytes ?? -1;
    case 'charged':
      return row.usage?.monthCharged ?? -1;
    default:
      return undefined;
  }
};

export function CellularNotices() {
  const { can } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const query = useQuery({ queryKey: ['cellular', 'notices'], queryFn: () => api.listCellularNotices() });
  const data = query.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const { sortedItems, sort, toggle } = useSort(rows, getValue, { key: 'charged', direction: 'desc' });
  const settings = data?.settings;

  const counts = useMemo(() => {
    const c = { undecided: 0, notify: 0, declined: 0, over: 0 };
    for (const r of rows) {
      const state = r.notice?.state ?? 'undecided';
      if (state === 'undecided') c.undecided++;
      if (state === 'notify') c.notify++;
      if (state === 'declined') c.declined++;
      if (r.usage?.excessive === 'over') c.over++;
    }
    return c;
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            Cellular · Notices
            {settings && (
              <InfoTip label="How notices work">
                <div className="space-y-1.5">
                  <p>
                    A customer is <span className="font-medium text-foreground">flagged</span> when it is in this
                    month’s top {settings.topN} — by data or by charges — and one of its sites is on 4G.
                  </p>
                  <p>
                    Choose <span className="font-medium text-foreground">Notify</span> and add recipients, or{' '}
                    <span className="font-medium text-foreground">Don’t notify</span> with a reason. Nothing is sent
                    to a customer until someone chooses Notify.
                  </p>
                  <p>
                    The {settings.sendTime} {settings.timeZone.replace('America/Los_Angeles', 'Pacific')} run sends
                    the first notice once a site has been on 4G {settings.minDaysOnFourG} days, then a reminder every{' '}
                    {settings.reminderDays} days. The case closes the first day the customer is back under 20
                    MB/day per site; a later failover opens a new one.
                  </p>
                  <p>
                    “Excessive” is more than ${settings.excessiveUsd} of cellular charges in a calendar month.
                  </p>
                </div>
              </InfoTip>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${monthLabel(data.month)} · usage from ${formatRelative(data.asOf)}` : 'Loading…'}
          </p>
        </div>
        {data && can('cellular.notify') && (
          <div className="flex items-center gap-2">
            <Select value={pick} onChange={(e) => setPick(e.target.value)} className="w-64" aria-label="Customer">
              <option value="">Notify another customer…</option>
              {data.customers.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Button variant="outline" size="sm" disabled={!pick} onClick={() => setSelected(pick)}>
              Open
            </Button>
          </div>
        )}
      </div>

      {settings && (settings.cc.length < 2 || !settings.safetyNetEmail || !settings.billingEmail) && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          The SafetyNet and Billing addresses are not configured yet (cdk.json <code>cellular.notices</code>), so
          notices are sent without those copies and name the teams without an address.
        </p>
      )}
      {query.error && <p className="text-sm text-destructive">{(query.error as Error).message}</p>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Undecided" value={counts.undecided} tone={counts.undecided ? 'caution' : undefined} />
        <Tile label="Notifying" value={counts.notify} />
        <Tile label="Not notifying" value={counts.declined} />
        <Tile label={`Over $${settings?.excessiveUsd ?? 100} this month`} value={counts.over} tone={counts.over ? 'warn' : undefined} />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-muted/50">
              <tr>
                <SortableHeader label="Customer" sortKey="customer" sort={sort} onToggle={toggle} />
                <SortableHeader label="Status" sortKey="state" sort={sort} onToggle={toggle} />
                <SortableHeader label="On 4G" sortKey="days" sort={sort} onToggle={toggle} />
                <SortableHeader label="Data this month" sortKey="data" sort={sort} onToggle={toggle} />
                <SortableHeader label="Charged this month" sortKey="charged" sort={sort} onToggle={toggle} />
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Next</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last emailed</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && <EmptyRow text="Loading…" />}
              {!query.isLoading && sortedItems.length === 0 && (
                <EmptyRow text="No customer is flagged — nobody in this month’s top 10 is on 4G." />
              )}
              {sortedItems.map((row) => {
                const u = row.usage;
                const state = row.notice?.state ?? 'flagged';
                return (
                  <tr
                    key={row.key}
                    onClick={() => setSelected(row.key)}
                    className={cn(
                      'cursor-pointer border-b border-line-soft last:border-0 hover:bg-raise',
                      (state === 'undecided' || state === 'flagged') && 'shadow-[inset_2px_0_0_var(--color-caution)]',
                      state === 'notify' && 'shadow-[inset_2px_0_0_var(--color-warn)]',
                    )}
                  >
                    <td className="px-4 py-2 font-medium">
                      {u?.label ?? row.notice?.label ?? row.key}
                      {u && (
                        <div className="text-xs font-normal text-muted-foreground">
                          #{u.rankByData ?? '—'} by data · #{u.rankByCharged ?? '—'} by charges
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <StateBadge state={state} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap tabular-nums">
                      {u?.onFourG ? (
                        <>
                          {u.daysOnFourG >= 31 ? '30d+' : u.daysOnFourG === 0 ? '<1d' : `${u.daysOnFourG}d`}
                          <span className="ml-1 text-xs text-muted-foreground">
                            {u.sites.filter((s) => s.onFourG).length} site{u.sites.filter((s) => s.onFourG).length === 1 ? '' : 's'}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">back on wired</span>
                      )}
                    </td>
                    <td className="px-4 py-2 tabular-nums">{formatData(u?.monthBytes)}</td>
                    <td className="px-4 py-2 tabular-nums">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {formatUsd(u?.monthCharged)}
                        {u && settings && <Excessive usage={u} threshold={settings.excessiveUsd} />}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <NextStepText next={row.next} sendTime={settings?.sendTime ?? '07:00'} />
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {row.notice?.lastEmailAt ? formatRelative(row.notice.lastEmailAt) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Drawer open={selected !== null} onClose={() => setSelected(null)} className="max-w-3xl" title={selected && <DrawerTitle customerKey={selected} rows={rows} />}>
        {selected && <NoticeDetail key={selected} customerKey={selected} canNotify={can('cellular.notify')} />}
      </Drawer>
    </div>
  );
}

function DrawerTitle({ customerKey, rows }: { customerKey: string; rows: Row[] }) {
  const row = rows.find((r) => r.key === customerKey);
  return (
    <div>
      <h2 className="text-base font-semibold">{row?.usage?.label ?? row?.notice?.label ?? customerKey}</h2>
      <p className="text-xs text-muted-foreground">Cellular notice</p>
    </div>
  );
}

function NoticeDetail({ customerKey, canNotify }: { customerKey: string; canNotify: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['cellular', 'notice', customerKey],
    queryFn: () => api.getCellularNotice(customerKey),
  });
  const data = query.data;
  const open = data?.history.find((n) => n.state !== 'resolved') ?? null;

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.error) return <p className="text-sm text-destructive">{(query.error as Error).message}</p>;
  if (!data) return null;
  const u = data.usage;

  return (
    <div className="space-y-5">
      {u && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Data this month" value={formatData(u.monthBytes)} />
          <Stat
            label="Charged this month"
            value={formatUsd(u.monthCharged)}
            sub={u.projectedCharged !== null ? `Projected ${formatUsd(u.projectedCharged)} (est.)` : undefined}
          />
          <Stat label="On 4G" value={u.onFourG ? (u.daysOnFourG >= 31 ? '30+ days' : `${u.daysOnFourG} days`) : 'No'} />
          <Stat label="Past 30 days" value={formatData(u.last30Bytes)} sub="cellular data" />
        </div>
      )}

      {canNotify ? (
        <DecisionForm
          customerKey={customerKey}
          open={open}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['cellular', 'notice', customerKey] });
            void queryClient.invalidateQueries({ queryKey: ['cellular', 'notices'] });
          }}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          {open ? `Status: ${STATE_LABEL[open.state]}.` : 'No open case.'} Deciding needs the “Notify customers about 4G
          use” permission.
        </p>
      )}

      {data.preview && (
        <div>
          <div className="flex items-center justify-between gap-2 pb-1.5">
            <h3 className="text-sm font-semibold">
              {data.preview.kind === 'reminder' ? 'Next reminder' : 'First notice'} as it will be sent
            </h3>
            {canNotify && <TestButton customerKey={customerKey} />}
          </div>
          <div className="space-y-0.5 pb-2 text-xs text-muted-foreground">
            <div>
              From <span className="text-fg-2">{data.settings.from}</span>
              {data.settings.cc.length > 0 && (
                <>
                  {' '}
                  · cc <span className="text-fg-2">{data.settings.cc.join(', ')}</span>
                </>
              )}
            </div>
            <div>
              Subject <span className="text-fg-2">{data.preview.subject}</span>
            </div>
          </div>
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={data.preview.html}
            className="h-[460px] w-full rounded-md border border-line bg-white"
          />
        </div>
      )}

      <Timeline history={data.history} />
    </div>
  );
}

function DecisionForm({
  customerKey,
  open,
  onSaved,
}: {
  customerKey: string;
  open: NoticeCase | null;
  onSaved: () => void;
}) {
  const [decision, setDecision] = useState<NoticeState>(open?.state === 'resolved' || !open ? 'undecided' : open.state);
  const [recipients, setRecipients] = useState<NoticeRecipient[]>(
    open?.recipients.length ? open.recipients : [{ name: '', email: '' }],
  );
  const [reason, setReason] = useState(open?.reason ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.decideCellularNotice(customerKey, {
        decision,
        recipients: recipients.filter((r) => r.name.trim() || r.email.trim()),
        ...(decision === 'declined' ? { reason } : {}),
      }),
    onSuccess: onSaved,
  });

  const options: { value: NoticeState; label: string }[] = [
    { value: 'notify', label: 'Notify' },
    { value: 'declined', label: 'Don’t notify' },
    { value: 'undecided', label: 'Undecided' },
  ];

  return (
    <Card className="space-y-3 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Decision</h3>
        <div role="group" aria-label="Decision" className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              aria-pressed={decision === o.value}
              onClick={() => setDecision(o.value)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                decision === o.value ? 'bg-accent-soft text-accent' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {decision === 'notify' && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Who at the customer receives the notice.</p>
          {recipients.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={r.name}
                placeholder="Name"
                onChange={(e) => setRecipients(recipients.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                className="w-48"
              />
              <Input
                value={r.email}
                type="email"
                placeholder="email@customer.com"
                onChange={(e) => setRecipients(recipients.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                className="flex-1"
              />
              <button
                type="button"
                aria-label="Remove recipient"
                onClick={() => setRecipients(recipients.filter((_, j) => j !== i))}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setRecipients([...recipients, { name: '', email: '' }])}>
            <Plus className="h-3.5 w-3.5" /> Add recipient
          </Button>
        </div>
      )}

      {decision === 'declined' && (
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this customer will not be notified (e.g. known ISP outage, already in contact)"
          rows={2}
        />
      )}

      <div className="flex items-center justify-end gap-3">
        {save.error && <span className="text-xs text-destructive">{(save.error as Error).message}</span>}
        {save.isSuccess && !save.isPending && <span className="text-xs text-muted-foreground">Saved</span>}
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save decision'}
        </Button>
      </div>
    </Card>
  );
}

function TestButton({ customerKey }: { customerKey: string }) {
  const send = useMutation({ mutationFn: () => api.testCellularNotice(customerKey) });
  return (
    <div className="flex items-center gap-2">
      {send.data && <span className="text-xs text-muted-foreground">Sent to {send.data.sentTo}</span>}
      {send.error && <span className="text-xs text-destructive">{(send.error as Error).message}</span>}
      <Button variant="outline" size="sm" onClick={() => send.mutate()} disabled={send.isPending}>
        <Mail className="h-3.5 w-3.5" />
        {send.isPending ? 'Sending…' : 'Send test to me'}
      </Button>
    </div>
  );
}

const EVENT_LABEL: Record<NoticeEvent['type'], string> = {
  opened: 'Case opened',
  decision: 'Decision',
  email: 'Emailed',
  'email-failed': 'Email failed',
  test: 'Test email',
  resolved: 'Resolved',
};

function Timeline({ history }: { history: NoticeCase[] }) {
  const events = history
    .flatMap((c) => c.events.map((e) => ({ ...e, caseOpened: c.openedAt })))
    .sort((a, b) => b.at.localeCompare(a.at));
  if (events.length === 0) return <p className="text-xs text-muted-foreground">No history yet.</p>;
  return (
    <div>
      <h3 className="pb-1.5 text-sm font-semibold">History</h3>
      <ol className="divide-y divide-line-soft border-t border-line-soft">
        {events.map((e, i) => (
          <li key={`${e.at}-${i}`} className="flex items-start justify-between gap-3 py-2 text-xs">
            <div className="min-w-0">
              <div className={cn('font-medium', e.type === 'email-failed' && 'text-destructive')}>
                {EVENT_LABEL[e.type]}
                <span className="ml-1.5 font-normal text-muted-foreground">by {e.actor ?? 'nightly run'}</span>
              </div>
              {e.detail && <div className="break-words text-fg-2">{e.detail}</div>}
              {e.messageId && <div className="font-mono text-[11px] text-muted-foreground">SES {e.messageId}</div>}
            </div>
            <span className="shrink-0 text-muted-foreground">{formatDateTime(e.at)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="rounded-md border border-line px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone?: 'warn' | 'caution' }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-2xl font-bold tabular-nums', tone === 'warn' && 'text-warn', tone === 'caution' && 'text-caution')}>
        {formatNumber(value)}
      </div>
    </Card>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <tr>
      <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
        {text}
      </td>
    </tr>
  );
}
